// ABOUTME: Holds a newly-arrived pinned chore list (Make It Right / Reset & Reflect / One-on-One
// ABOUTME: Time) out of view for a grace period, so it doesn't instantly reorder the dashboard

import { useEffect, useRef, useState } from 'react';
import { ChoreListPeriodStatus, ChoreListType } from '../types/api';
import { CHORE_LIST_DELAY_MS } from '../utils/choreListUtils';

const PINNED_TYPES = new Set([ChoreListType.MakeItRight, ChoreListType.ResetAndReflect, ChoreListType.OneOnOneTime]);

function msSinceCreated(choreList: ChoreListPeriodStatus): number {
  if (!choreList.createdAt) return Infinity; // no timestamp — treat as already elapsed
  return Date.now() - new Date(choreList.createdAt).getTime();
}

/**
 * Returns choreStatuses with brand-new pinned lists omitted until CHORE_LIST_DELAY_MS has
 * elapsed since their createdAt timestamp (wall-clock, not time-since-first-seen — a list
 * created 4s ago only waits the remaining ~1s even if this hook only just mounted). Regular
 * lists and lists already seen in a prior render are never delayed (issue #1916).
 */
export function useDelayedChoreListView(choreStatuses: ChoreListPeriodStatus[]): ChoreListPeriodStatus[] {
  // settledIds is React state (not a ref) so it's safe to read during render — a ref's `.current`
  // must only be touched in effects/handlers, never in the render body or return value.
  const [settledIds, setSettledIds] = useState<Set<number>>(() => new Set());
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const timers = timersRef.current;
    for (const choreList of choreStatuses) {
      if (!PINNED_TYPES.has(choreList.type)) continue;
      if (settledIds.has(choreList.id) || timers.has(choreList.id)) continue;

      const elapsed = msSinceCreated(choreList);
      if (elapsed >= CHORE_LIST_DELAY_MS) {
        // Already past the delay — the render-time filter below already shows it directly via
        // msSinceCreated, no state update needed (and none allowed synchronously in an effect).
        continue;
      }

      const remaining = CHORE_LIST_DELAY_MS - elapsed;
      const timer = setTimeout(() => {
        setSettledIds(prev => new Set(prev).add(choreList.id));
        timers.delete(choreList.id);
      }, remaining);
      timers.set(choreList.id, timer);
    }

    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [choreStatuses, settledIds]);

  // Check elapsed time directly (not just settledIds) so a list whose delay has already passed
  // shows up on the very first render, before the effect above has had a chance to run.
  return choreStatuses.filter(choreList =>
    !PINNED_TYPES.has(choreList.type) ||
    settledIds.has(choreList.id) ||
    msSinceCreated(choreList) >= CHORE_LIST_DELAY_MS
  );
}
